import {
  LitElement,
  html,
  css,
  classMap,
  ifDefined,
  live,
} from '@hh/global.js';

class HHSwitch extends LitElement {
  static properties = {
    id: {
      type: String,
    },
    name: {
      type: String,
    },
    disabled: {
      type: Boolean,
      reflect: true,
    },
    checked: {
      type: Boolean,
      reflect: true,
    },
    required: {
      type: Boolean,
      reflect: true,
    },
    ariaLabel: {
      type: String,
      reflect: true,
      attribute: 'aria-label',
    },
    enableZeroOneToggle: {
      type: Boolean,
      attribute: 'enable-zero-one-toggle',
    },
  }

  constructor() {
    super()
  }

  connectedCallback() {
    super.connectedCallback()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
  }

  updated(changedProperties) {
    if (changedProperties.has('checked')) {
      this.onCheckedChange()
    }

    if (changedProperties.has('disabled')) {
      this.onDisabledChange()
    }
  }

  onClick() {
    this.checked = !this.checked
    this.dispatchEvent(new Event('change'))
  }

  onKeyDown(e) {
    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      this.checked = false
      this.dispatchEvent(new Event('change'))
    }

    if (e.key === 'ArrowRight') {
      e.preventDefault()
      this.checked = true
      this.dispatchEvent(new Event('change'))
    }
  }

  onCheckedChange() {
    this.input.checked = this.checked

    if (!this.hiddenInput) return

    if (this.enableZeroOneToggle) {
      this.hiddenInput.value = this.checked ? '1' : '0'
    } else {
      this.hiddenInput.value = this.checked ? 'on' : 'off'
    }
    this.dispatchEvent(new Event('change', { bubbles: true }))
  }

  onDisabledChange() {
    this.input.disabled = this.disabled
  }

  click() {
    this.input.click()
  }

  get input() {
    return this.renderRoot?.querySelector('input') ?? null
  }

  get hiddenInput() {
    return (
      this.renderRoot
        ?.querySelector('slot[name="input"]')
        ?.assignedNodes()[0] ?? null
    )
  }

  render() {
    return html`
      <label
        class=${classMap({
          switch: true,
          'switch--checked': this.checked,
          'switch--disabled': this.disabled,
        })}
      >
        <input
          id=${this.id}
          class="switch__input"
          type="checkbox"
          name=${this.name}
          value=${ifDefined(this.value)}
          .checked=${live(this.checked)}
          .disabled=${this.disabled}
          .required=${this.required}
          role="switch"
          aria-label=${ifDefined(this.ariaLabel)}
          @click=${this.onClick}
          @keydown=${this.onKeyDown}
        />

        <span class="switch__control">
          <span class="switch__thumb"></span>
        </span>

        <slot name="label" class="switch__label"></slot>
      </label>
      <slot name="input"></slot>
    `
  }

  static styles = css`
    :host {
      display: inline-block;
      box-sizing: border-box;
    }

    :host *,
    :host *::before,
    :host *::after {
      box-sizing: inherit;
    }

    .switch {
      position: relative;
      display: inline-flex;
      align-items: center;
      font: var(--hh-font, inherit);
      vertical-align: middle;
      cursor: pointer;
      column-gap: var(--hh-gap, 0.8rem);
    }

    .switch__control {
      flex: 0 0 auto;
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: var(--hh-width, 4rem);
      height: var(--hh-height, 2.4rem);
      background-color: var(--hh-background-color);
      border: var(--hh-border-width, 0.1rem) solid var(--hh-background-color);
      border-radius: var(--hh-border-radius, 1.2rem);
      transition: border-color var(--hh-duration-medium),
        background-color var(--hh-duration-medium);
    }

    .switch__control .switch__thumb {
      width: var(--hh-thumb-size);
      height: var(--hh-thumb-size);
      background-color: var(--hh-thumb-color);
      border-radius: 50%;
      border: var(--hh-border-width, 0.1rem) solid var(--hh-background-color);
      translate: calc((var(--hh-width) - var(--hh-height)) / -2);
      transition: var(--hh-duration-medium) translate ease,
        var(--hh-duration-medium) background-color,
        var(--hh-duration-medium) border-color;
    }

    .switch__input {
      position: absolute;
      opacity: 0;
      padding: 0;
      margin: 0;
      pointer-events: none;
    }

    .switch--checked .switch__control {
      background-color: var(--hh-checked-background-color, #39aa40);
      border-color: var(--hh-checked-background-color, #39aa40);
    }

    .switch--checked .switch__control .switch__thumb {
      border-color: var(--hh-checked-background-color, #39aa40);
      translate: calc((var(--hh-width) - var(--hh-height)) / 2);
    }

    .switch--disabled .switch__control {
      background-color: var(--hh-disabled-background-color, #e6e6e6);
      border-color: var(--hh-disabled-background-color, #e6e6e6);
      cursor: default;
    }

    .switch--disabled .switch__control .switch__thumb {
      border-color: var(--hh-disabled-background-color, #e6e6e6);
    }

    .switch.switch--disabled,
    .switch--disabled .switch__label {
      cursor: default;
    }

    .switch:not(.switch--disabled)
      .switch__input:focus-visible
      + .switch__control {
      outline: 0.2rem solid var(--hh-color-focus);
      outline-offset: var(--hh-focus-outline-offset);
    }

    :host([aria-label]) .switch__label {
      display: none;
    }
  `
}

if (!customElements.get('hh-switch')) {
  customElements.define('hh-switch', HHSwitch);
}
